import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Cable, CheckCircle2, CreditCard, Monitor, Package, PackagePlus, Truck,
  Paperclip, Megaphone, Wrench, RefreshCw,
} from 'lucide-react';
import type { UnitStatus } from '@platform/shared-types';
import ShimmerImage from '../../../components/ShimmerImage';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { toastService } from '../../../services/toastService';
import { installMutationWatcher, useDataChanged, DataVersionContext } from '../../../services/dataChangeBus';
import PackOrderPanel from '../../inventory/PackOrderPanel';
import MerchantPanel from '../../inventory/MerchantPanel';
import VarSheetBindingPanel from '../../inventory/VarSheetBindingPanel';
import TillHandoverPanel from '../../inventory/TillHandoverPanel';
import { ShippedStagePanel } from '../../inventory/FinalStagePanels';
import OrderStages from '../../inventory/OrderStages';
import OrderLocationCard, { type LocationContext } from '../../inventory/OrderLocationCard';
import { merchantApi, type MerchantApplicationView } from '../../../services/merchantApi';
import { journeyApi, type ManualCompletions } from '../../../services/journeyApi';
import { api } from '../../../services/apiService';
import PaymentVerifyPanel from '../../inventory/PaymentVerifyPanel';
import LocationPaidPanel from '../../inventory/LocationPaidPanel';
import JourneyTimeline from '../../inventory/JourneyTimeline';
import CancellationPanel from './CancellationPanel';
import ProvisioningStagePanel from '../../inventory/ProvisioningStagePanel';
import DocumentMarks from '../../inventory/DocumentMarks';
import AllocateStagePanel from '../../inventory/AllocateStagePanel';
import CoupleChecklistStage from '../../inventory/CoupleChecklistStage';
import PairReceipts from '../../inventory/PairReceipts';
import AgreementPackagePanel from '../../inventory/AgreementPackagePanel';
import CommissionPanel from '../../inventory/CommissionPanel';
import DeviceTracker, { ConfirmUnitsPanel } from '../../inventory/DeviceTracker';
import OrderActivationPanel from '../../inventory/OrderActivationPanel';
import { PlanPanel, PaymentBreakdown, useSoftwareMonthlyCents } from '../../inventory/PlanAndPaymentPanels';
import OrderPlacedPanel from '../../inventory/OrderPlacedPanel';
import PendingReplacementBanners from '../../inventory/PendingReplacementBanners';
import { ReplaceWizardHost } from '../../inventory/replaceWizardHost';
import {
  RegisterEmvPanel, CreateRegisterPanel, DeviceCouplePanel, PaymentTypesPanel, ButtonLayoutPanel,
  EmvConfigStepsPanel, registerForUnit,
} from '../../inventory/DeviceSetupPanels';
import PackageActivationQuickView from '../../../components/tenant/PackageActivationQuickView';
import {
  needsTerminalId, deviceLane, displayOrderNumber, CANCELLATION_STAGES_V1,
  type JourneyContext, type SettlementLine, type CancellationRecord, type SettlementTotals,
} from '@platform/shared-types';
import {
  hardwareApi,
  cancelBlockReason,
  derivedOrderStatusView,
  orderBillingLabel,
  orderMoney,
  saveBlockReason,
  shipmentForIntent,
  shortDate,
  unitTransitionBlock,
  EXCEPTION_FLOW,
  FULFILMENT_FLOW,
  ORDER_STATUS_CHIP,
  ORDER_STATUS_LABEL,
  REASON_REQUIRED,
  SHIPMENT_REQUIRED,
  UNIT_STATUS_CHIP,
  UNIT_STATUS_LABEL,
  type DeviceBundleRow,
  type DeviceModelRow,
  type DeviceOrderRow,
  type DeviceUnitRow,
  type ShipmentDraft,
  type UnitIntent,
} from '../../../services/hardwareApi';
import { RubyLoader } from '../../../components/RubyLoader';
import { PaymentTypesSaveHub } from '../../../components/pos/payment-types/SaveHub';

/**
 * ONE device order, opened for fulfilment.
 *
 * Two rules shape this screen, and both are also enforced by the service — the
 * UI is never the only guard, and it must never contradict them:
 *
 *   1. Status is per DEVICE, not per order. Customers want to know where each
 *      device is, not where the paperwork is. The order's own status is DERIVED
 *      from the devices and is shown with its `partial` flag spelled out, so
 *      nobody reads "shipped" and misses that a device is still backordered.
 *   2. Save stays disabled until a shipped status has a carrier AND a tracking
 *      number, and until a hold or backorder has a reason — that reason is the
 *      message the customer gets.
 *
 * And one thing the service does that this screen must not fight: an incorrect
 * `shipment.shippedAt` is permanent. Once a device is stamped as shipped it can
 * never return to a pre-dispatch state, so those segments are DISABLED rather
 * than offered and then refused.
 */

const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor,
  payment_device: CreditCard,
  peripheral: Package,
  accessory: Cable,
  office_supply: Paperclip, marketing_material: Megaphone, tools: Wrench,
};

/** Whole class strings so Tailwind's scanner can see them — never interpolate. */
const SEGMENT_ON: Record<string, string> = {
  awaiting: 'bg-gray-600 text-white',
  processing: 'bg-amber-500 text-white',
  provisioning: 'bg-rose-600 text-white',
  configured: 'bg-rose-700 text-white',
  packing: 'bg-blue-600 text-white',
  shipped: 'bg-rose-600 text-white',
  delivered: 'bg-emerald-600 text-white',
  deployed: 'bg-emerald-700 text-white',
};

const CARRIERS = ['UPS', 'FedEx', 'USPS', 'DHL', 'OnTrac'];

/** Two decimals, always — the header quotes money an operator reads aloud. */
const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

interface Draft {
  state: UnitStatus;
  serialNumber: string;
  holdReason: string;
}

const draftOf = (u: DeviceUnitRow): Draft => ({
  state: u.state,
  serialNumber: u.serialNumber || '',
  holdReason: u.holdReason || '',
});

/**
 * The shipment already on record, if the devices agree on one. Prefilled so an
 * operator does not have to re-key a carrier to move a device from shipped to
 * delivered; left blank when the devices went out separately, because guessing
 * one of two tracking numbers would silently overwrite the other.
 */
function existingShipment(units: DeviceUnitRow[]): ShipmentDraft {
  const shipped = units.filter((u) => u.shipment?.carrier && u.shipment?.trackingNumber);
  if (shipped.length === 0) return { carrier: '', trackingNumber: '', trackingUrl: '' };
  const first = shipped[0].shipment!;
  const agree = shipped.every(
    (u) => u.shipment!.carrier === first.carrier && u.shipment!.trackingNumber === first.trackingNumber,
  );
  return agree
    ? { carrier: first.carrier, trackingNumber: first.trackingNumber, trackingUrl: first.trackingUrl || '' }
    : { carrier: '', trackingNumber: '', trackingUrl: '' };
}

/**
 * What the order says it bought, expanded to device models: a bundle item is
 * its components, an item naming a model is that model. Items naming neither
 * cannot be turned into devices here and are reported rather than dropped.
 */
function expectedDevices(order: DeviceOrderRow, bundles: DeviceBundleRow[]) {
  const wanted = new Map<string, number>();
  let unresolved = 0;
  for (const item of order.items || []) {
    const qty = item.quantity || 1;
    if (item.modelId) {
      wanted.set(item.modelId, (wanted.get(item.modelId) || 0) + qty);
      continue;
    }
    const bundle = item.bundleId ? bundles.find((b) => b.bundleId === item.bundleId) : undefined;
    if (!bundle) { unresolved += qty; continue; }
    for (const c of bundle.components || []) {
      wanted.set(c.modelId, (wanted.get(c.modelId) || 0) + qty * (c.quantity || 1));
    }
  }
  return { wanted, unresolved };
}

/** Device models on the order that have no unit row yet, so nothing can be packed. */
function missingDevices(order: DeviceOrderRow, units: DeviceUnitRow[], bundles: DeviceBundleRow[]) {
  const { wanted, unresolved } = expectedDevices(order, bundles);
  const have = new Map<string, number>();
  for (const u of units) have.set(u.modelId, (have.get(u.modelId) || 0) + 1);

  const missing: { modelId: string; quantity: number }[] = [];
  wanted.forEach((qty, modelId) => {
    const shortfall = qty - (have.get(modelId) || 0);
    if (shortfall > 0) missing.push({ modelId, quantity: shortfall });
  });
  return { missing, unresolved };
}

interface Props {
  orderId: string;
  models: DeviceModelRow[];
  bundles: DeviceBundleRow[];
  tenantName: string;
  /** Reload the list behind this card — the filter counts move with the statuses. */
  onChanged: () => void;
  onClose: () => void;
}

/**
 * `packages_activated`'s panel — a button rather than the modal opening on
 * mount, because `OrderStages` expands every stage by default (only
 * `merchant_assigned` starts closed — see its `CLOSED_BY_DEFAULT`). Anything
 * that opened the quick view as a mount side effect would pop it on every
 * order page load, not on a click.
 */
const PackagesActivatedStagePanel: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <div className="text-[12.5px] text-stone-600 leading-relaxed">
    <p className="mb-2">
      What they paid for becoming what the software turned on — enabled packages against what was
      billed, the run history, and (if it happened) why activation fell short.
    </p>
    <button
      onClick={onOpen}
      className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-2 text-[11px] font-semibold text-white hover:bg-rose-700"
    >
      Open package activation
    </button>
  </div>
);

/**
 * THE STAGES WHOSE WORK IS DONE ON THE COUPLE CARD.
 *
 * Hidden from this screen's rail, never removed from the journey — see where
 * `hideStageIds` is passed for why that distinction matters.
 */
const COUPLE_OWNED_STAGES = [
  // The setup three — worked as steps on the pair's card.
  'till_handover', 'payment_types', 'button_layout',
  // The proof three. The refund receipt, the receipt-printing check and the
  // barcode check are all per PAIR — a test sale needs a till and its reader,
  // and "scan either box and get both" is a fact about a couple, not about an
  // order. All three are gathered under the pair on Test transaction, so the
  // order-wide copies are the duplicates.
  'refund_transaction', 'receipt_printing', 'barcode_scanning',
] as const;

/** "Live · 8s ago" — re-renders itself so the age stays true between polls. */
const LiveSince: React.FC<{ at: number }> = ({ at }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(t);
  }, []);
  const s = Math.max(0, Math.round((now - at) / 1000));
  const age = s < 10 ? 'just now' : s < 60 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500" title="This order updates by itself while the tab is open">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live · {age}
    </span>
  );
};

/** How often an open order checks for changes made somewhere else. */
const LIVE_POLL_MS = 12_000;

/** What has to move for an open order to be worth re-reading. */
function orderSignature(order: DeviceOrderRow | null | undefined, units: Array<{ unitId: string; state?: string; updatedAt?: number | string }>): string {
  if (!order) return '';
  const u = units
    .map((x) => `${x.unitId}:${x.state || ''}:${x.updatedAt || ''}`)
    .sort()
    .join('|');
  return `${order.status}:${(order as any).updatedAt || ''}#${u}`;
}

const OrderDetailBody: React.FC<Props> = ({ orderId, models, bundles, tenantName, onChanged, onClose }) => {
  const [order, setOrder] = useState<DeviceOrderRow | null>(null);
  // What the stage timeline needs beyond the order itself: whose store this is,
  // what they have sent us, and where their card processing stands. Each is
  // optional — a failure to read one must not take the order screen with it.
  const [locationCtx, setLocationCtx] = useState<LocationContext | null>(null);
  const [merchantApp, setMerchantApp] = useState<MerchantApplicationView | null>(null);
  /**
   * The PERSISTED half of the journey — the stage snapshot and the manual
   * completions, with who made them. Evidence-backed stages need none of this;
   * they are resolved from facts already on this screen.
   */
  const [tenantMeta, setTenantMeta] = useState<any>(null);
  /*
    THE TWO SETUP FACTS THE LANE SUMMARIES NEED.

    The overview shows a percentage per lane, and a percentage nobody can
    trust is worse than none — so "which readers are in the pool" and "which
    tills have a register" are read here rather than only inside the panels
    that do the work. Two list calls, both best-effort: a failure leaves those
    lanes reading UNANSWERED (the resolver's `todo`), never wrongly complete.

    Everything heavier still waits for its lane to be opened.
  */
  const [emvPool, setEmvPool] = useState<any[] | null>(null);
  const [registers, setRegisters] = useState<any[] | null>(null);
  /** The owner handover record, when it exists — completes `till_handover`. */
  const [tillHandover, setTillHandover] = useState<{ at: number } | null>(null);
  /** Bumped by refreshSetup so the handover is re-read after the panel acts. */
  const [setupTick, setSetupTick] = useState(0);
  /*
    EVERY SAVE ON THIS PAGE REFRESHES THE WHOLE PAGE, QUIETLY.

    Twenty-odd panels each read their own data, and a save in one never reached
    the others — a VAR sheet uploaded in the card lane was invisible to the
    reader waiting to bind it until somebody reloaded a very long page. The
    watcher sees every successful save on the wire, whatever panel or service
    made it; after the burst settles the order re-reads in the background and
    `dataVersion` goes up, which the side lookups below and every panel that
    reads `useDataVersion()` refetch on. Nothing blanks, nothing scrolls.
  */
  const [dataVersion, setDataVersion] = useState(0);
  useEffect(() => installMutationWatcher(), []);

  /*
    REFRESH WHAT CHANGED, NOT THE WHOLE ORDER.

    Every setup action used to call the order's own `load()`. That refetches
    the order, the units and the bundles, remounts the rail, and throws the
    reader back to wherever the page happens to land — so ticking one step or
    creating one register cost you your place, and on a long order you could
    not tell what had just happened.

    A register or a pool entry only affects the two lists the lane summaries
    read, so those are refetched alone; a unit comes BACK from the call that
    changed it, so it is patched in place and nothing is refetched at all.
  */
  const refreshSetup = useCallback(() => {
    if (!order?.tenantId) return;
    setSetupTick((n) => n + 1);
    api.posTerminals.list(order.tenantId)
      .then((r: any) => setRegisters(r?.terminals || []))
      .catch(() => { /* the lane keeps its last known answer */ });
    if (order.locationId) {
      api.posNmi.listEmvDevicesAtLocation(order.tenantId, order.locationId)
        .then((r: any) => setEmvPool(r?.devices || r?.items || (Array.isArray(r) ? r : [])))
        .catch(() => { /* the lane keeps its last known answer */ });
    }
  }, [order?.tenantId, order?.locationId]);

  /* ── the compliance pack, from the stage that is looking at it ─────────── */
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);

  /** Open one document. The URL is minted per click and expires in minutes. */
  const openComplianceDoc = useCallback(async (documentId: string) => {
    const requestId = (locationCtx?.request as any)?.requestId;
    if (!requestId || !order?.tenantId) return;
    try {
      const res: any = await api.locationBilling.getDocumentViewUrl(requestId, order.tenantId, documentId);
      const url = res?.url || res?.viewUrl;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not open that document.');
    }
  }, [locationCtx, order?.tenantId]);

  /**
   * Put a document up FOR the merchant.
   *
   * It lands as `uploaded`, never `verified` — supplying the evidence and
   * blessing it in one click is the check this pack exists to be, so somebody
   * still opens it and approves on the Registrations tab.
   */
  const uploadComplianceDoc = useCallback(async (documentId: string, file: File) => {
    const requestId = (locationCtx?.request as any)?.requestId;
    if (!requestId || !order?.tenantId) return;
    setUploadingDoc(documentId);
    try {
      const res: any = await api.locationBilling.uploadDocumentForTenant(requestId, {
        tenantId: order.tenantId, documentId, file,
      });
      const saved = res?.document;
      /* Patched in place from what the service just returned — the mark flips
         to "uploaded" at once and nothing is refetched. */
      setLocationCtx((prev) => (prev?.request
        ? {
          ...prev,
          request: {
            ...prev.request,
            documents: ((prev.request as any).documents || []).map((d: any) =>
              (d.documentId === documentId ? { ...d, ...(saved || { status: 'uploaded' }) } : d)),
          } as any,
        }
        : prev));
      toastService.success('Uploaded. It still needs approving on Registrations.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not upload that document.');
    } finally {
      setUploadingDoc(null);
    }
  }, [locationCtx, order?.tenantId]);

  /**
   * APPROVE OR REJECT, from the order.
   *
   * The pack sat here reading "uploaded — awaiting review" with no way to
   * review it: the decision lived on the Registrations screen, so whoever was
   * working the order had to go and find the same documents somewhere else.
   * The PARTNER now has the same two controls on their own screen; both write
   * the same two statuses, so one document cannot mean two things.
   */
  const [decidingDoc, setDecidingDoc] = useState<string | null>(null);
  const decideComplianceDoc = useCallback(async (
    documentId: string, action: 'approve' | 'reject', comment?: string,
  ) => {
    const requestId = (locationCtx?.request as any)?.requestId;
    if (!requestId || !order?.tenantId) return;
    setDecidingDoc(documentId);
    try {
      await api.locationBilling.documentAction(requestId, {
        tenantId: order.tenantId, documentId, action, comment,
      });
      // Patched in place from what we just asked for — same as the upload
      // path, so the mark flips without a refetch.
      setLocationCtx((prev) => (prev?.request
        ? {
          ...prev,
          request: {
            ...prev.request,
            documents: ((prev.request as any).documents || []).map((d: any) => (d.documentId === documentId
              ? { ...d, status: action === 'approve' ? 'verified' : 'rejected', ...(comment ? { comment } : {}) }
              : d)),
          } as any,
        }
        : prev));
      toastService.success(action === 'approve' ? 'Approved.' : 'Rejected — the merchant has been told why.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not record that decision.');
    } finally {
      setDecidingDoc(null);
    }
  }, [locationCtx, order?.tenantId]);

  /** Take a file off a document so a new copy can go up. The requirement stays. */
  const clearComplianceDoc = useCallback(async (documentId: string) => {
    const requestId = (locationCtx?.request as any)?.requestId;
    if (!requestId || !order?.tenantId) return;
    setUploadingDoc(documentId);
    try {
      const res: any = await api.locationBilling.clearDocumentFile(requestId, documentId, order.tenantId);
      const saved = res?.document;
      // Replaced, not merged: the cleared fields arrive as null and must win.
      setLocationCtx((prev) => (prev?.request
        ? {
          ...prev,
          request: {
            ...prev.request,
            documents: ((prev.request as any).documents || []).map((d: any) =>
              (d.documentId === documentId
                ? (saved || { ...d, status: 'requested', fileKey: null, fileName: null, uploadedAt: null })
                : d)),
          } as any,
        }
        : prev));
      toastService.success('File removed. Upload a new copy when ready.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not remove that file.');
    } finally {
      setUploadingDoc(null);
    }
  }, [locationCtx, order?.tenantId]);

  /** One unit, as the service just returned it. No refetch, no remount. */
  /*
    THE SERVER'S ROW REPLACES OURS — it does not merge into it.

    This used to be `{ ...x, ...updated }`, which cannot express a field that
    has been CLEARED: JSON has no way to send `undefined`, so a removed
    `coupledUnitId` simply arrives as an absent key and the spread kept the old
    value. Breaking a device pair therefore worked on the server and stayed on
    screen until a reload — the request succeeded, the row updated, and the UI
    quietly merged the deletion away.

    Every caller passes the full unit the service just wrote (`res.unit`, the
    parsed row), so that row IS the truth and anything it omits is genuinely
    gone. Merging was only ever hiding that.
  */
  const patchUnit = useCallback((updated: DeviceUnitRow) => {
    setUnits((prev) => prev.map((x) => (x.unitId === updated.unitId ? updated : x)));
  }, []);
  const [journeyId, setJourneyId] = useState<string | null>(null);
  const [manual, setManual] = useState<ManualCompletions>({});
  /**
   * `action_block_run` audit rows for this tenant — read for exactly one
   * thing: resolving `journeyContext.packageActivation` below, so the
   * `packages_activated` stage can actually go red. Fetched once per tenant
   * alongside `tenantMeta`; a failure here must not take the order screen
   * down, so it is swallowed and the stage simply falls back to `todo`.
   */
  const [activationRuns, setActivationRuns] = useState<any[]>([]);
  // The Stripe invoice that proves this order was paid — see the invoice read below.
  const [paidInvoice, setPaidInvoice] = useState<{ paidAt: number; amountPaid: number } | null>(null);
  /**
   * MIRRORED FROM `CancellationPanel`, not fetched separately.
   *
   * That panel already owns the one read of this order's cancellation record;
   * a second fetch here would just be a second place this could disagree with
   * it. `null` means either "nothing has loaded yet" or "no cancellation on
   * this order" — both read identically to the rail: no `cx_*` stages.
   */
  const [cancellationSummary, setCancellationSummary] = useState<{
    cancellation: CancellationRecord | null; totals: SettlementTotals | null;
  }>({ cancellation: null, totals: null });

  const [units, setUnits] = useState<DeviceUnitRow[]>([]);
  const [invalidUnits, setInvalidUnits] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [shipment, setShipment] = useState<ShipmentDraft>({ carrier: '', trackingNumber: '', trackingUrl: '' });
  const [shipmentEdited, setShipmentEdited] = useState(false);
  /** True when every already-shipped device shares one shipment, so these fields describe all of them. */
  const [shipmentPrefilled, setShipmentPrefilled] = useState(false);
  /** Mirrors `shipmentEdited` for `load`, which must not re-create itself (and
   *  so re-fire the mount effect) every time a tracking field is touched. */
  const shipmentEditedRef = useRef(shipmentEdited);
  useEffect(() => { shipmentEditedRef.current = shipmentEdited; }, [shipmentEdited]);
  const [carrierOptions, setCarrierOptions] = useState<string[]>(CARRIERS);
  const [loading, setLoading] = useState(true);
  /** A refetch that happens UNDER the rendered order rather than instead of it. */
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  /** Packing & boxes (mockup screen D) — a toggled panel rather than its own
   * route, since it needs nothing this screen doesn't already have loaded. */
  const [packOpen, setPackOpen] = useState(false);
  /** The `packages_activated` quick view (Task 7) — see PackagesActivatedStagePanel. */
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const openPackagesQuickView = useCallback(() => setQuickViewOpen(true), []);

  /**
   * EVERYTHING THE STAGES ARE RESOLVED FROM.
   *
   * Assembled here rather than fetched by the timeline, because every piece is
   * already on this screen: the order, its units, the envelope the order
   * carries, the store's compliance pack and the merchant application. The
   * resolver reads facts — it never asks anyone whether a stage is done.
   *
   * When journey-service lands this object is what it will build server-side,
   * and this component will receive the resolved stages instead.
   */
  /* Was the till handed over — the same record TillHandoverPanel restores from. */
  const handoverEmail = (tenantMeta as any)?.ownerEmail || (tenantMeta as any)?.email;
  useEffect(() => {
    if (!order?.tenantId || !handoverEmail) return;
    let live = true;
    api.posAuth.getHandover(order.tenantId, { ownerEmail: handoverEmail })
      .then((r) => { if (live) setTillHandover(r?.handover?.at ? { at: r.handover.at } : null); })
      .catch(() => { /* stays todo — a missed read must not look handed over */ });
    return () => { live = false; };
  }, [order?.tenantId, handoverEmail, setupTick]);

  const journeyContext = useMemo((): JourneyContext => {
    const byModel = new Map(models.map((m) => [m.modelId, m]));

    /*
      WHICH LANE A DEVICE BELONGS TO — its CATALOGUE CATEGORY, and nothing else.

      One question, asked once, answered the same way for all three lanes:
        payment_device -> Payment devices
        pos_terminal   -> POS devices
        anything else  -> Peripherals & accessories

      The two faults this replaces:

      1. The payment lane was keyed on `needsTerminalId`, which is the DISPATCH
         GATE — "must this unit hold a terminal id before it ships" — and honours
         a per-model `requiresTerminalId` override. An Android till with a card
         path set that flag, and was thereby pulled out of the POS lane (which
         filtered `!paymentUnitIds`) and into the payment lane. Two different
         questions were sharing one answer. `needsTerminalId` keeps its real job
         below and in MerchantPanel; it no longer decides where a device is
         drawn.

      2. The accessories lane was a WHITELIST of `peripheral` and `accessory`,
         so `office_supply`, `marketing_material`, `tools` — and any category
         added to the catalogue after this was written — landed on no lane at
         all and were invisible on this screen. It is the catch-all now, which
         is what "Peripherals & accessories" has to mean if every device is to
         appear exactly once.

      A model MISSING from the catalogue still lands nowhere, deliberately: with
      no category there is nothing to route on, and a lane that reads unanswered
      is recoverable where one that wrongly reads done is not.
    */
    const laneOf = (modelId?: string) => deviceLane(byModel.get(modelId || '') as any);

    const paymentUnitIds = units
      .filter((u) => laneOf(u.modelId) === 'payment')
      .map((u) => u.unitId);
    const needed = (order?.items || []).reduce((n: number, i: any) => n + (i.quantity || 1), 0);

    /*
      How many card devices the ORDER calls for, counted from its lines and
      bundles rather than from units. Before allocation there are no units, and
      counting only those made a brand-new order carrying a PIN pad read as
      "no card devices" — on the screen where somebody has to notice that an
      ISO needs assigning.

      STILL `needsTerminalId`, DELIBERATELY, now that the lanes are drawn by
      category. This number does not place anything — it answers "will this
      order need a terminal id at all", which is the TID question and therefore
      the one `needsTerminalId` exists for. A till that sets `requiresTerminalId`
      is drawn on the POS lane (its category) and still counts here, so the card
      stages stay alive for it; `resolveJourney` skips them only when this count
      AND the payment lane are both empty, so the divergence can only ever keep
      a stage visible, never hide one.
    */
    const expectedPaymentDeviceCount = (order?.items || []).reduce((n: number, item: any) => {
      const qty = item.quantity || 1;
      if (item.bundleId) {
        const bundle = bundles.find((b) => b.bundleId === item.bundleId);
        return n + (bundle?.components || []).reduce(
          (m: number, c: any) => m + (needsTerminalId(byModel.get(c.modelId) as any) ? (c.quantity || 1) * qty : 0), 0);
      }
      return n + (needsTerminalId(byModel.get(item.modelId) as any) ? qty : 0);
    }, 0);

    /*
      WHICH `activate_packages` RUN THIS STAGE READS.

      1. The most recent run STAMPED WITH THIS ORDER'S id — billing-service
         stamps `orderId` on the run a checkout completion triggers, so this is
         the run that belongs to this sale.
      2. Otherwise the most recent `activate_packages` run for the tenant at
         all (manual re-runs from the Packages tab carry no `orderId` but are
         still the truth about whether the packages are on).
      3. Otherwise `packageActivation` stays undefined and the stage reads
         `todo` — never invent a status from no run.

      The run row (`ActionBlockRun`, billing-service's `recordRun`) carries no
      package-id list — only `packagesToActivate` on the onboarding
      completion's own stored result does, which this screen does not read —
      so `packages` is deliberately omitted below rather than faked.
    */
    const activationCandidates = activationRuns
      .filter((r: any) => r?.blockId === 'activate_packages')
      .sort((a: any, b: any) => (b.at ?? b.timestamp ?? 0) - (a.at ?? a.timestamp ?? 0));
    // `order?.orderId` guarded explicitly — `undefined === undefined` would
    // otherwise match a run that carries no `orderId` at all before `order`
    // has loaded, on the very first render of this memo.
    const activationRun = (order?.orderId
      ? activationCandidates.find((r: any) => r.orderId === order.orderId)
      : undefined) || activationCandidates[0];

    return {
      order: order ? { orderId: order.orderId, createdAt: order.createdAt, items: order.items as any } : undefined,
      // Nothing was ever passed here, so `Paid` read "Nothing collected yet" on
      // every order — including ones Stripe had already charged.
      payment: paidInvoice ? { paid: true, paidAt: paidInvoice.paidAt } : undefined,
      paymentUnitIds,
      // The other side of the split, so the hardware stage counts only its own.
      hardwareUnitIds: units.filter((u) => !paymentUnitIds.includes(u.unitId)).map((u) => u.unitId),
      /*
        THE FORK'S OTHER TWO LANES — see `laneOf`. A till and a receipt printer
        are different work, on different lanes, finishing at different times.
      */
      posUnitIds: units
        .filter((u) => laneOf(u.modelId) === 'pos')
        .map((u) => u.unitId),
      /*
        THE PAIRS, read off the units themselves.

        Both halves carry the link, so this needs no lookup — and listing a
        pair only from the POS side keeps one row per pair instead of two
        rows saying the same thing from opposite ends.
      */
      couples: units
        .filter((u) => !!u.coupledUnitId && laneOf(u.modelId) === 'pos')
        .map((u) => ({ posUnitId: u.unitId, paymentUnitId: u.coupledUnitId! })),
      /* A reader is in the pool when a pool entry carries its serial — the
         only field the unit and the pool record share. */
      emvRegisteredUnitIds: emvPool
        ? units
          .filter((u) => !!u.serialNumber
            && emvPool.some((d: any) => String(d?.serialNumber || '').trim() === u.serialNumber!.trim()))
          .map((u) => u.unitId)
        : undefined,
      /* Which readers have had all three on-terminal steps asserted — the app
         installed, the parameters set up, the parameters pulled down. Matched
         by serial like the line above, since that is the only field a unit and
         its pool record share. A device with two of three is deliberately NOT
         here: partly configured is not configured. */
      emvConfiguredUnitIds: emvPool
        ? units
          .filter((u) => {
            const dev = u.serialNumber
              ? emvPool.find((d: any) => String(d?.serialNumber || '').trim() === u.serialNumber!.trim())
              : undefined;
            const s = dev?.configSteps || {};
            return !!(s.appInstalled && s.paramsSetup && s.paramsDownloaded);
          })
          .map((u) => u.unitId)
        : undefined,
      /* A register belongs to a unit by its `deviceUnitId` — the same
         `registerForUnit` the couple card uses. Matching on the NAME alone
         (the old rule, now only the fallback for registers minted before the
         field) read a register renamed "K10-TEST" as missing, so the POS lane
         sat at "still needs a register" with every register in place. */
      registers: registers
        ? units.flatMap((u) => {
          const t: any = registerForUnit(registers as any[], u);
          return t ? [{
            terminalId: t.terminalId,
            provisionCode: t.provisionCode,
            unitId: u.unitId,
            posSetupAt: typeof t.posSetupAt === 'number' ? t.posSetupAt : undefined,
          }] : [];
        })
        : undefined,
      // Never passed before, so Payment types read "takes nothing yet" forever.
      paymentTypesByTerminal: registers
        ? Object.fromEntries(registers.map((t: any) => [t.terminalId, Array.isArray(t.paymentTypes) ? t.paymentTypes : []]))
        : undefined,
      tillHandover: tillHandover || undefined,
      /* Everything that is neither a payment device nor a POS device — see
         `laneOf`. The catch-all, so no device on the order is drawn nowhere. */
      accessoryUnitIds: units
        .filter((u) => laneOf(u.modelId) === 'accessory')
        .map((u) => u.unitId),
      expectedPaymentDeviceCount,
      units: units.map((u) => ({ unitId: u.unitId, state: u.state, updatedAt: (u as any).updatedAt })),
      neededUnitCount: needed || undefined,
      envelopeId: order?.envelopeId,
      envelopeStatus: order?.envelopeStatus,
      compliance: locationCtx?.request ? { documents: locationCtx.request.documents } : undefined,
      tenant: tenantMeta ? {
        businessName: (tenantMeta as any).name || (tenantMeta as any).businessName,
        legalName: locationCtx?.request?.legalEntityName,
        ownerEmail: (tenantMeta as any).ownerEmail || (tenantMeta as any).email,
        createdAt: (tenantMeta as any).createdAt,
      } : (locationCtx?.request ? { legalName: locationCtx.request.legalEntityName } : undefined),
      origin: locationCtx ? {
        tenantCreatedAt: (tenantMeta as any)?.createdAt,
        locationCreatedAt: locationCtx.locationCreatedAt,
        locationCount: locationCtx.locationCount,
      } : undefined,
      merchant: merchantApp ? {
        status: merchantApp.status,
        merchantSource: merchantApp.merchantSource,
        assignedIsoId: merchantApp.assignedIsoId,
        assignedIsoName: merchantApp.assignedIsoName,
        assignedAt: merchantApp.assignedAt,
        submittedAt: merchantApp.submittedAt,
        approvedAt: merchantApp.approvedAt,
        varSheets: merchantApp.varSheets,
        terminalAssignments: merchantApp.terminalAssignments,
      } : undefined,
      // Packing, shipping, verification: human tasks, held by journey-service
      // with the name and role of whoever did them.
      manual,
      // Mirrored from CancellationPanel — see cancellationSummary above.
      // `policyEnvelopeId` stays undefined: Plan 2's policy attach never
      // shipped, and every live order has no signed policy to name.
      cancellation: cancellationSummary.cancellation ? {
        status: cancellationSummary.cancellation.status,
        origin: cancellationSummary.cancellation.origin,
        raisedAt: cancellationSummary.cancellation.raisedAt,
        acceptedAt: cancellationSummary.cancellation.acceptedAt,
        closedAt: cancellationSummary.cancellation.closedAt,
        refundableCents: cancellationSummary.totals?.refundableCents,
        policyEnvelopeId: undefined,
      } : undefined,
      // What they paid for becoming what the software turned on — see
      // `activationRun` above. `packages` omitted: the run row carries no
      // package-id list to read one from.
      packageActivation: activationRun ? {
        status: activationRun.status,
        at: activationRun.at ?? activationRun.timestamp,
        failedSteps: (activationRun.steps ?? [])
          .filter((s: any) => !s.ok)
          .map((s: any) => s.step),
      } : undefined,
    };
  }, [order, units, models, bundles, locationCtx, merchantApp, manual, tenantMeta, cancellationSummary, activationRuns, emvPool, registers, paidInvoice, tillHandover]);

  /**
   * WHETHER THE RAIL SHOULD DRAW THE EIGHT CANCELLATION STAGES.
   *
   * `cx_*` ids land on a journey's `stageIds` all at once, the instant a
   * cancellation opens (journey-service `cancellation.ts`) — never partially
   * — so "a cancellation record exists at all" and "the journey snapshot
   * carries the cx_ ids" are the same fact. Reading it off `cancellationSummary`
   * (which CancellationPanel keeps live) avoids a second, staler source: the
   * `stageIds` this screen's own one-time `journeyApi.ensure()` call captured
   * would still read empty for the rest of this visit if a cancellation opens
   * after the page loaded.
   */
  const journeyStageIds = useMemo(
    () => (cancellationSummary.cancellation ? CANCELLATION_STAGES_V1.map((s) => s.id) : []),
    [cancellationSummary.cancellation],
  );

  /**
   * THE SETTLEMENT WORKSHEET LINES — one per device model on this order.
   *
   * A device order (`DeviceOrderRow`) carries only hardware line items
   * (`DeviceOrderItemSchema`: skuId/bundleId/modelId/quantity/saleMode/
   * amountCents) — there is no separate software-package or up-front-fee
   * line on this record, so every line here is `group: 'devices'`. Lines
   * naming the same model are combined so "2 × K10" is one row, matching the
   * mockup, rather than one row per unit.
   *
   * `paidCents` is approximated as the order's own `amountCents × quantity`
   * — the actual amount billed to date on a financed device lives on its
   * `DeviceUnitRow.agreement`, not on the order, and this screen has no
   * per-payment ledger to read instead. `refundableCents` mirrors it: with
   * no signed cancellation policy, nothing says otherwise is withheld.
   * `penaltyCents`, `owedCents` and `ruleLabel` are fixed at 0 / 0 /
   * "No policy · 0" on every line, per the settlement worksheet spec — this
   * is not a placeholder, it is the correct and permanent value until a
   * signed policy exists to read instead.
   */
  const cancellationLines = useMemo((): SettlementLine[] => {
    if (!order) return [];
    const byKey = new Map<string, { modelId?: string; skuId: string; saleMode: string; quantity: number; amountCents: number }>();
    for (const it of order.items || []) {
      const key = it.modelId || it.bundleId || it.skuId;
      const qty = it.quantity || 1;
      const existing = byKey.get(key);
      if (existing) {
        existing.quantity += qty;
        existing.amountCents += (it.amountCents || 0) * qty;
      } else {
        byKey.set(key, { modelId: it.modelId, skuId: it.skuId, saleMode: it.saleMode, quantity: qty, amountCents: (it.amountCents || 0) * qty });
      }
    }
    return Array.from(byKey.entries()).map(([key, v]) => {
      const model = v.modelId ? models.find((m) => m.modelId === v.modelId) : undefined;
      const label = model ? `${v.quantity} × ${model.manufacturer} ${model.modelName}` : `${v.quantity} × ${v.skuId}`;
      return {
        lineId: `line_${key}`,
        group: 'devices' as const,
        label,
        detail: `${v.saleMode} · $${(v.amountCents / Math.max(v.quantity, 1) / 100).toFixed(2)} each`,
        paidCents: v.amountCents,
        refundableCents: v.amountCents,
        penaltyCents: 0,
        owedCents: 0,
        ruleLabel: 'No policy · 0',
      };
    });
  }, [order, models]);

  /**
   * Refetch the order.
   *
   * `{ background: true }` keeps the operator on the page: no spinner swap, no
   * remounted rail, and nothing half-typed is thrown away — the new data just
   * arrives under them. Allocating a device is the case this exists for.
   * Reserving one unit used to blank the entire order behind a full-card
   * spinner and land the reader back at the top, for a change that moves one
   * row's counters. Same reasoning as `refreshSetup` above, applied to the one
   * refetch that genuinely does need the whole order back.
   *
   * A background refresh also must not lose a failed load: an error still
   * surfaces, it just does not take the rendered order down with it.
   */
  const load = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    if (background) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await hardwareApi.getOrder(orderId);
      setOrder(res.order);
      setUnits(res.units || []);
      setInvalidUnits(res.invalidUnits || []);
      // Keep the draft already on screen for a unit we still have — a
      // background refresh must not stomp a serial someone is mid-way through
      // typing — and seed one for every unit we have just met.
      setDrafts((prev) => {
        const next: Record<string, Draft> = {};
        for (const u of res.units || []) next[u.unitId] = (background && prev[u.unitId]) || draftOf(u);
        return next;
      });
      const ship = existingShipment(res.units || []);
      // Unsaved carrier/tracking edits outrank the server's copy on a
      // background pass; a foreground load is a fresh start either way.
      if (!(background && shipmentEditedRef.current)) {
        setShipment(ship);
        setShipmentEdited(false);
        setShipmentPrefilled(!!ship.carrier && !!ship.trackingNumber);
      }
      // A confirmation left hanging from before a reload would be asking about
      // changes that no longer exist. A background refresh is not a reload —
      // an open confirm there belongs to the operator, not to the fetch.
      if (!background) {
        setConfirmClose(false);
        setConfirmCancel(false);
      }
      setCarrierOptions((prev) => (ship.carrier && !prev.includes(ship.carrier) ? [...prev, ship.carrier] : prev));
    } catch (e: any) {
      const msg = e?.message || 'Failed to load this order';
      // `error` replaces the whole card. That is right when we have nothing to
      // show, and wrong when the order is already on screen and a refresh
      // failed — the operator would lose a working page over a flaky refetch.
      if (background) toastService.error(msg);
      else setError(msg);
    } finally {
      if (background) setRefreshing(false); else setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  /**
   * THE ONLY RELOAD A PANEL GETS IS A QUIET ONE.
   *
   * Panels were handed `onChanged={load}`, and `load()` with no options is the
   * FOREGROUND load: the whole order swapped for the page loader, every panel
   * remounted, the reader thrown back to the top — on top of the background
   * re-read the mutation watcher had already started for the same save. After
   * the first paint nothing on this screen should ever blank it again.
   */
  const reload = useCallback(() => load({ background: true }), [load]);

  /** The order + unit state last read, and when — see LIVE below. */
  const signatureRef = useRef('');
  const [lastSyncedAt, setLastSyncedAt] = useState(() => Date.now());

  /** Re-read the order AND tell every panel to refetch its own data. */
  const refreshAll = useCallback(async () => {
    await load({ background: true });
    setDataVersion((v) => v + 1);
    setLastSyncedAt(Date.now());
  }, [load]);

  useDataChanged(() => { refreshAll(); });

  /*
    LIVE, WITHOUT ANYONE ASKING.

    The mutation watcher only hears saves made from THIS tab. Half of what moves
    an order happens elsewhere — a till finishing its install in the store, a
    tick from the installer, a colleague on another screen — and none of that
    reached an open order until somebody reloaded the page.

    So while the tab is visible the order is polled; the panels are only told to
    refetch when the order or one of its units actually CHANGED (their
    `updatedAt`/state signature), so an idle page costs one small read per tick
    rather than twenty panels refetching. Coming back to the tab refreshes
    everything, since anything at all may have moved while it was hidden.
  */
  useEffect(() => {
    if (!order) return;
    signatureRef.current = orderSignature(order, units);
  }, [order, units]);

  useEffect(() => {
    let busy = false;
    const tick = async () => {
      if (busy || document.visibilityState !== 'visible') return;
      busy = true;
      try {
        const res = await hardwareApi.getOrder(orderId);
        const next = orderSignature(res.order, res.units || []);
        if (next !== signatureRef.current) await refreshAll();
        else setLastSyncedAt(Date.now());
      } catch { /* a missed poll is retried on the next tick; the page stays as it is */ }
      finally { busy = false; }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refreshAll(); };
    const timer = window.setInterval(tick, LIVE_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [orderId, refreshAll]);

  /**
   * The `action_block_run` audit rows `journeyContext.packageActivation`
   * resolves from. Same endpoint the quick-view modal reads — no new
   * endpoint. Pulled out as its own callback (rather than inline in the
   * effect below) so it can ALSO be handed to the modal as `onRan` — after a
   * successful "Run activation" the modal needs this page's copy refreshed
   * too, or the timeline pill keeps showing the pre-retry state until a full
   * reload. That stage's hint text says "Red here means the customer has
   * been charged and cannot use what they bought" — a false "still broken"
   * after the operator just fixed it is exactly the failure this exists to
   * prevent.
   *
   * `limit: 200`, well above the endpoint's default 50: rows are filtered
   * client-side for `kind === 'action_block_run'` (and, in the memo below,
   * further to `blockId === 'activate_packages'`) AFTER the server applies
   * the limit. A narrow window can silently crowd the tenant's activation
   * run out of a 50-row page of unrelated audit history, making the stage
   * read `todo` when a run actually exists — a false negative on the exact
   * signal this feature exists to surface.
   *
   * Fail-open on both the initial load and a post-retry refresh: the catch
   * leaves `activationRuns` exactly as it was rather than clearing it, so a
   * flaky refetch after a successful run cannot regress a correct stage back
   * to `todo`, and the order screen keeps rendering either way.
   */
  /**
   * RE-READ THE MERCHANT APPLICATION WHEN THIS TAB COMES BACK.
   *
   * It was fetched once, on mount. The partner works their side in their own
   * portal while this page sits open on somebody's second monitor, so a sheet
   * they upload, a TID they key or a reader they mark ready never appeared
   * here — the operator saw "0 sheets on file" on an application that had
   * one, and the only cure was a full reload nobody knew to do.
   *
   * On focus rather than on a timer: the question is only ever asked when
   * somebody looks, and a poll would run all day for the few seconds that
   * matter. `ensureApplication` is idempotent, so this is a read.
   */
  useEffect(() => {
    const tenantId = order?.tenantId;
    const locationId = order?.locationId;
    if (!tenantId || !locationId) return undefined;

    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      merchantApi.ensureApplication(tenantId, locationId)
        .then((r) => setMerchantApp(r.application))
        .catch(() => { /* keep whatever we last read; never blank the lane */ });
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [order?.tenantId, order?.locationId]);

  const loadActivationRuns = useCallback(async (tenantId: string) => {
    try {
      const res: any = await api.superAdmin.getTenantAuditLog(tenantId, 200);
      const events = Array.isArray(res?.events) ? res.events : [];
      setActivationRuns(events.filter((e: any) => e?.kind === 'action_block_run'));
    } catch {
      // Leave `activationRuns` as-is — see doc comment above.
    }
  }, []);

  /*
    Open the journey for this order, or pick up the one that exists.

    Idempotent — the id derives from the order — so calling it on every open is
    what backfills orders placed before the backbone existed. A failure leaves
    manual stages unattributed and everything else working: the timeline is
    important, but it must not be able to take the order screen down.
  */
  useEffect(() => {
    if (!order?.orderId || !order?.tenantId) return;
    let live = true;
    // Who signed up, and when — the account stage and the origin read this.
    api.tenants.getMetadata(order.tenantId, { silent: true })
      .then((m) => { if (live) setTenantMeta(m); })
      .catch(() => { /* the stage falls back to the legal entity on the request */ });

    /*
      WAS IT PAID — read from Stripe's own invoices, not inferred.

      The order's own `invoiceId` when it has one (add-on orders). Otherwise the
      first PAID invoice from checkout onwards: an onboarding order is written
      as checkout completes, so its first charge lands within moments of
      `createdAt` — the hour of slack covers the invoice being stamped just
      before the order row. A failed read leaves the stage `todo`, never green.
    */
    api.superAdmin.getTenantInvoices(order.tenantId, 50)
      .then((res: any) => {
        if (!live) return;
        const paid = (res?.invoices || []).filter((i: any) => i.status === 'paid' && i.amountPaid > 0 && i.paidAt);
        const own = (order as any).invoiceId ? paid.find((i: any) => i.id === (order as any).invoiceId) : undefined;
        const fromCheckout = paid
          .filter((i: any) => i.paidAt >= (order.createdAt || 0) - 60 * 60 * 1000)
          .sort((a: any, b: any) => a.paidAt - b.paidAt)[0];
        const hit = own || fromCheckout;
        setPaidInvoice(hit ? { paidAt: hit.paidAt, amountPaid: hit.amountPaid } : null);
      })
      .catch(() => { /* stays todo — a missed read must not look paid */ });

    /*
      RE-DERIVE A UNIT STATE WHOSE INPUTS CHANGED AFTER IT WAS COMPUTED.

      `provisioning -> configured` is decided when a checklist step is ticked,
      and a card device is held back there until a TID is keyed to it. Nothing
      re-evaluated that once the TID landed, so a reader keyed AFTER its last
      tick stayed `provisioning` for good — and `provision_payment`, which needs
      every payment unit at `configured`, sat at 4/5 with all five genuinely
      done. Asked again here, on the load that is about to draw those lanes.

      Best-effort and silent: it only moves units forward, only ones whose
      required steps are all done, and it runs the same gates the tick does. A
      failure leaves the lane reading exactly as it did before.
    */
    hardwareApi.recheckProvisioning(order.orderId)
      .then((r) => { if (live && r?.advanced?.length) load({ background: true }); })
      .catch(() => { /* the lane keeps its last known answer */ });

    // The two setup lookups the lane summaries need. Both best-effort: a
    // failure leaves that lane unanswered rather than wrongly complete.
    api.posTerminals.list(order.tenantId)
      .then((r: any) => { if (live) setRegisters(r?.terminals || []); })
      .catch(() => { /* the POS lane reads as not-yet-known */ });
    if (order.locationId) {
      api.posNmi.listEmvDevicesAtLocation(order.tenantId, order.locationId)
        .then((r: any) => { if (live) setEmvPool(r?.devices || r?.items || (Array.isArray(r) ? r : [])); })
        .catch(() => { /* the payment lane reads as not-yet-known */ });

      /*
        THE MERCHANT APPLICATION IS A LANE SUMMARY FACT, not a panel's private
        state.

        It used to arrive ONLY through `MerchantPanel`'s `onApplication`
        callback — and that panel mounts only when its stage is expanded. So on
        a fresh load with the card lane collapsed, `merchant` was undefined and
        all three of its stages resolved to nothing: the lane read 0% on an
        order whose sheet was bound and whose partner was settled, and
        `configure_payment` reported "still needs a TID" for a reader that had
        one. Expanding the stage silently fixed it, which is worse than the
        wrong number — the same order read two different ways depending on
        what the operator had clicked.

        `ensureApplication` is idempotent (the id derives from the location),
        which is why it is safe to call here as well as in the panel. The
        panels still push fresher copies up through `onApplication`/`onChanged`.
      */
      merchantApi.ensureApplication(order.tenantId, order.locationId)
        .then((r) => { if (live) setMerchantApp(r.application); })
        .catch(() => { /* the card lane reads as not-yet-known, never complete */ });
    }
    if (live) loadActivationRuns(order.tenantId);
    journeyApi.ensure(order.tenantId, order.orderId, { locationId: order.locationId })
      .then((res) => {
        if (!live) return;
        setJourneyId(res.journey.journeyId);
        setManual(res.manual || {});
      })
      .catch((e) => console.error('[OrderDetail] journey unavailable — manual stages will not persist', e?.message));
    return () => { live = false; };
    // `dataVersion`: re-run every side lookup after any save on the page.
  }, [order?.orderId, order?.tenantId, order?.locationId, loadActivationRuns, dataVersion]);

  const modelOf = useCallback(
    (modelId: string) => models.find((m) => m.modelId === modelId),
    [models],
  );

  const patch = (unitId: string, change: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [unitId]: { ...prev[unitId], ...change } }));

  /**
   * The order's status as it IS — derived from what is actually stored on the
   * devices. This is the only thing the header chip and the status panel are
   * allowed to report. An unsaved draft rendered as fact would tell the operator
   * their kit is on its way while part of it is not: the exact failure this
   * design exists to prevent, moved from the customer to the operator.
   */
  const savedStates = useMemo(() => units.map((u) => u.state), [units]);
  const derived = useMemo(
    () => derivedOrderStatusView(order?.status, savedStates),
    [order?.status, savedStates],
  );

  /**
   * THE TWO HALVES OF THE MONTHLY BILL.
   *
   * `hardware` is this order's own recurring lines; `softwareMonthlyCents` is
   * what billing composes for the tenant's stored plan assignment, or `null`
   * when that is not a confirmed figure. The header adds them — see there for
   * why it refuses to when the software half is unknown.
   */
  const hardware = useMemo(() => orderMoney(order?.items || []), [order?.items]);
  // `dataVersion` passed in: this hook runs OUTSIDE the page's own
  // DataVersionContext.Provider, so it cannot read the context itself.
  const softwareMonthlyCents = useSoftwareMonthlyCents(order?.tenantId, dataVersion);

  /**
   * The store's NAME, once the location card below has resolved it. Until then
   * (and for an order whose location has no record) the header falls back to
   * the raw `locationId` rather than going blank — an unresolvable store is
   * still a fact the operator needs to see.
   */
  const locationName = locationCtx?.request?.name || locationCtx?.location?.name || '';

  /**
   * How the history should READ the ids it recorded. The entries themselves are
   * append-only and untouched — this only names what they point at, and only
   * what we can actually resolve.
   */
  const timelineLabels = useMemo(() => {
    const m: Record<string, string> = {};
    if (order?.orderId && order.orderNo) m[order.orderId] = displayOrderNumber(order.orderNo);
    if (order?.locationId && locationName) m[order.locationId] = locationName;
    if (order?.tenantId && tenantName) m[order.tenantId] = tenantName;
    return m;
  }, [order?.orderId, order?.orderNo, order?.locationId, order?.tenantId, locationName, tenantName]);

  /** The same thing computed from the DRAFTS — a preview, and labelled as one. */
  const targetStates = useMemo(
    () => units.map((u) => drafts[u.unitId]?.state ?? u.state),
    [units, drafts],
  );
  const preview = useMemo(
    () => derivedOrderStatusView(order?.status, targetStates),
    [order?.status, targetStates],
  );

  /**
   * May an edit to the dispatch fields REWRITE tracking a device already has?
   *
   * Only when the fields were prefilled from a shipment every shipped device on
   * this order shares — then they describe all of them and correcting one wrong
   * tracking number corrects it everywhere. When the devices went out
   * separately the fields start blank, describe none of them, and must never be
   * pushed over the tracking numbers that are already right.
   */
  const shipmentGoverns = shipmentEdited && shipmentPrefilled;

  /** Only the devices this Save would actually write. An untouched one must never block. */
  const pending = useMemo<UnitIntent[]>(() => {
    const out: UnitIntent[] = [];
    for (const unit of units) {
      const d = drafts[unit.unitId];
      if (!d) continue;
      const intent: UnitIntent = { unit, state: d.state, holdReason: d.holdReason };
      const stateMoved = d.state !== unit.state;
      const serialMoved = d.serialNumber.trim() !== (unit.serialNumber || '').trim();
      const reasonMoved = REASON_REQUIRED.has(d.state) && d.holdReason.trim() !== (unit.holdReason || '').trim();
      // A dispatch edit only counts as a change when what we would send actually
      // differs from what the device already carries.
      const ship = shipmentForIntent(intent, shipment, shipmentGoverns);
      const shipmentMoved = !!ship && (
        ship.carrier !== unit.shipment?.carrier
        || ship.trackingNumber !== unit.shipment?.trackingNumber
        || (ship.trackingUrl || '') !== (unit.shipment?.trackingUrl || '')
      );
      if (stateMoved || serialMoved || reasonMoved || shipmentMoved) out.push(intent);
    }
    return out;
  }, [units, drafts, shipment, shipmentGoverns]);

  /** Would saving actually move the order's status? Only then is a preview worth showing. */
  const previewDiffers = pending.length > 0
    && (preview.status !== derived.status || preview.partial !== derived.partial);

  const blocked = useMemo(
    () => (pending.length === 0 ? null : saveBlockReason(pending, shipment, order?.status)),
    [pending, shipment, order?.status],
  );

  const cancelBlocked = useMemo(
    () => (order ? cancelBlockReason(order, units) : 'Loading…'),
    [order, units],
  );

  const shipping = pending.some((i) => SHIPMENT_REQUIRED.has(i.state) && !SHIPMENT_REQUIRED.has(i.unit.state));


  // -- actions ---------------------------------------------------------------

  const save = async () => {
    if (!order || blocked || pending.length === 0) return;
    setSaving(true);
    const failures: string[] = [];
    try {
      for (const intent of pending) {
        const d = drafts[intent.unit.unitId];
        const name = modelOf(intent.unit.modelId)?.modelName || intent.unit.modelId;
        try {
          // The serial belongs to the device, not to the transition — write it
          // first so a refused status change still keeps the corrected serial.
          if (d.serialNumber.trim() !== (intent.unit.serialNumber || '').trim()) {
            await hardwareApi.upsertUnit({ unitId: intent.unit.unitId, serialNumber: d.serialNumber.trim() });
          }
          const ship = shipmentForIntent(intent, shipment, shipmentGoverns);
          const stateMoved = intent.state !== intent.unit.state;
          const reasonMoved = REASON_REQUIRED.has(intent.state)
            && intent.holdReason.trim() !== (intent.unit.holdReason || '').trim();
          if (stateMoved || reasonMoved || ship) {
            await hardwareApi.setUnitStatus(intent.unit.unitId, {
              state: intent.state,
              ...(ship ? { shipment: ship } : {}),
              ...(REASON_REQUIRED.has(intent.state) ? { holdReason: intent.holdReason.trim() } : {}),
            });
          }
        } catch (e: any) {
          failures.push(`${name}: ${e?.message || 'refused'}`);
        }
      }
    } finally {
      setSaving(false);
    }

    if (failures.length === 0) {
      toastService.success(`${pending.length} device${pending.length === 1 ? '' : 's'} updated. Where the new status has a customer email, it has gone out from Rubytill.`);
    } else {
      // A partial failure must never look like success.
      failures.forEach((f) => toastService.error(f));
    }
    await reload();
    onChanged();
  };


  const cancelOrder = async () => {
    if (!order || cancelBlocked) return;
    setConfirmCancel(false);
    try {
      await hardwareApi.cancelOrder(order.orderId);
      toastService.success('Order cancelled.');
    } catch (e: any) {
      toastService.error(e?.message || 'Could not cancel this order');
    }
    await reload();
    onChanged();
  };

  // -- render ----------------------------------------------------------------

  if (loading) {
    return (
      <div className="rounded-xl border-2 bg-white" style={{ borderColor: '#DC143C' }}>
        <RubyLoader variant="page" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="rounded-xl border-2 border-red-300 bg-white p-5 text-gray-900">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error || 'Order not found'}</div>
        </div>
        <button onClick={onClose} className="mt-4 min-h-[44px] rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700">
          Close
        </button>
      </div>
    );
  }

  /* Saved states only — a draft problem is not yet something the order has. */
  const problemUnits = units.filter((u) => EXCEPTION_FLOW.includes(u.state));

  return (
    <DataVersionContext.Provider value={dataVersion}>
    {/* Light card: the text colour is set HERE, on the container. index.html
       sets `body { color: white }`, so a light card without it is invisible. */}
    <div className="rounded-xl border-2 bg-white p-5 text-gray-900" style={{ borderColor: '#DC143C' }}>
      {/* A replacement started and not finished — first thing on the order,
          with the only way back into it. */}
      <PendingReplacementBanners order={order} units={units} onChanged={() => load({ background: true })} />
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-gray-900">{tenantName}</span>
            {/* Derived from the SAVED device states — the chip states what the
                order is, never what an unsaved draft would make it. */}
            <span className={`rounded px-2 py-0.5 text-[11px] font-semibold tracking-wide ${ORDER_STATUS_CHIP[derived.status] || 'bg-gray-200 text-gray-700'}`}>
              {(ORDER_STATUS_LABEL[derived.status] || derived.status).toUpperCase()}
            </span>
            {derived.partial && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-amber-800">
                PARTIAL
              </span>
            )}
            {problemUnits.length > 0 && (
              <span className="rounded bg-red-100 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-red-700">
                {problemUnits.length} DEVICE{problemUnits.length === 1 ? '' : 'S'} WITH A PROBLEM
              </span>
            )}
            {/* The order is refetching underneath — said quietly, so the page
                stays put and readable while it happens. */}
            {refreshing && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400">
                <RubyGemMark size={11} drawing /> Updating
              </span>
            )}
            {/* Live — the order polls itself; the button is there for whoever
                wants to be sure, and never blanks the page. */}
            <span className="ml-auto inline-flex items-center gap-2">
              <LiveSince at={lastSyncedAt} />
              <button
                type="button"
                onClick={() => { refreshAll(); }}
                disabled={refreshing}
                title="Re-read this order and every panel on it"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 text-[12px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw size={13} /> Refresh
              </button>
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-gray-500">
            {/*
              The HUMAN number leads. `orderId` is the key every unit, envelope
              and invoice references, so it stays — but nobody reads a UUID
              down the phone during a claim two years from now.
            */}
            {order.orderNo
              ? (
                /* The raw `orderId` moves into the tooltip rather than out of
                   the page: it is still what units, envelopes and invoices
                   reference, but a UUID beside the number people actually
                   quote only crowds the line. */
                <span className="font-mono font-semibold text-stone-800" title={order.orderId}>
                  {displayOrderNumber(order.orderNo)}
                </span>
              )
              : <span className="font-mono" title={order.orderId}>{order.orderId}</span>}
            {/* The STORE, by its name. `loc_1789…` tells an operator nothing,
                and the card below has already resolved it. */}
            {order.locationId && (
              <>
                {' · '}
                <span title={order.locationId}>{locationName || order.locationId}</span>
              </>
            )}
            {' · '}placed {shortDate(order.createdAt)}
          </p>
          {/* A device problem is an ORDER fact — whoever opens this sees it first. */}
          {problemUnits.length > 0 && (
            <div role="status" className="mt-2 space-y-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
              {problemUnits.map((u) => (
                <p key={u.unitId} className="text-[12px] text-stone-700">
                  <b className="text-red-700">{modelOf(u.modelId)?.modelName || u.modelId}</b>
                  {' is '}{UNIT_STATUS_LABEL[u.state].toLowerCase()}
                  {u.holdReason ? <> — “{u.holdReason}”</> : ''}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-start gap-4">
          {/*
            WHAT THE CUSTOMER PAYS EVERY MONTH — BOTH HALVES OF IT.

            This read "Billing $138.98/mo", which was the HARDWARE alone. Beside
            an order screen that is entirely about hardware, that number reads
            as the subscription, and it is not: the software the tenant is on is
            the larger half of most of these bills. An operator quoting it down
            the phone quotes a figure the invoice does not contain.

            So the headline is the sum, labelled as the sum, with the two parts
            underneath in small letters — because the parts are what an operator
            is asked about ("what am I paying for the terminals?") and a total
            with no breakdown just moves the same problem.

            WHEN THE SOFTWARE HALF IS NOT KNOWN it says so by reverting to the
            hardware label it always had, rather than presenting hardware as a
            total. `useSoftwareMonthlyCents` returns null — never 0 — for an
            unconfirmed figure, precisely so this branch is reachable.
          */}
          <div className="text-right">
            {softwareMonthlyCents === null ? (
              <>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Hardware billing</div>
                <div className="text-sm font-bold text-gray-900">{orderBillingLabel(order.items)}</div>
                <div className="text-[10px] text-gray-400">Software not priced yet</div>
              </>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Total subscription</div>
                <div className="text-sm font-bold text-gray-900">
                  {money(softwareMonthlyCents + hardware.monthlyCents)}/mo
                </div>
                <div className="text-[10px] leading-snug text-gray-500">
                  Software {money(softwareMonthlyCents)} · Hardware {money(hardware.monthlyCents)}
                </div>
                {hardware.onceCents > 0 && (
                  <div className="text-[10px] leading-snug text-gray-400">
                    + {money(hardware.onceCents)} one-time
                  </div>
                )}
              </>
            )}
          </div>
          {order.status !== 'cancelled' && (
            <button
              onClick={() => setPackOpen((o) => !o)}
              className="min-h-[44px] rounded-lg px-3 text-xs font-semibold text-white hover:opacity-90"
              style={{ background: packOpen ? '#9F1239' : '#DC143C' }}
            >
              <Package size={13} className="inline mr-1.5 -mt-0.5" /> {packOpen ? 'Hide packing' : 'Pack this order'}
            </button>
          )}
          <button
            onClick={() => (pending.length > 0 ? setConfirmClose(true) : onClose())}
            className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {packOpen && <PackOrderPanel orderId={order.orderId} models={models} onChanged={() => { load({ background: true }); onChanged(); }} />}

      {/* Closing with unsaved device changes throws them away. Say so rather than
          discarding an operator's work on a stray click — and, like every other
          confirmation here, do it in the page: a native confirm() is banned and
          is silently a no-op inside a webview. */}
      {confirmClose && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900">
          <span>
            <strong>Discard {pending.length} unsaved device change{pending.length === 1 ? '' : 's'}?</strong>{' '}
            Nothing has been sent to the customer, and closing loses what you have typed.
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmClose(false)}
              className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 text-xs font-semibold text-gray-700"
            >
              Keep editing
            </button>
            <button
              onClick={() => { setConfirmClose(false); onClose(); }}
              className="min-h-[44px] rounded-lg bg-amber-600 px-4 text-xs font-semibold text-white"
            >
              Discard and close
            </button>
          </div>
        </div>
      )}

      {invalidUnits.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-xs">
            <strong>{invalidUnits.length} stored device{invalidUnits.length === 1 ? '' : 's'} failed validation</strong> and
            {invalidUnits.length === 1 ? ' is' : ' are'} hidden: <span className="font-mono">{invalidUnits.join(', ')}</span>.
            They are still on the order — fix them before it can be completed.
          </div>
        </div>
      )}

      {/*
        THE ORDER AS STAGES.

        The list is data (`ORDER_STAGES_V1`), so a new verification or packaging
        step later is a change to that list rather than to this screen. Each
        stage owns the panel that does its work — which is why the merchant
        panel and the location card are passed IN rather than stacked below.
      */}
      {order && (() => {
      /*
        THIS STAGE'S CHECKLIST, FOR THE DEVICES IT CONCERNS.

        Provisioning steps are written per model against the stage they are
        worked at (/inventory/provisioning), so each stage draws its own and
        nothing else: coupling checks under coupling, packing instructions
        under packing, the last look under Shipped. Before this, every step a
        device had appeared once, in a pile, under the configure stage.

        No `only`/`lane`/`unitIds` scope is passed: the stage filter is the
        scope here, and the panel drops any device with nothing to do at this
        stage — so a coupling step on a till does not drag the cash drawer
        onto the screen with it.
      */
      const stageChecklist = (stageId: string) => (
        <ProvisioningStagePanel
          units={units}
          models={models}
          items={order.items as any}
          bundles={bundles as any}
          stageIds={[stageId]}
          paymentUnitIds={journeyContext.paymentUnitIds}
          onUnitChanged={patchUnit}
          onChanged={reload}
        />
      );
      return (
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 items-start">
          <OrderStages
            context={journeyContext}
            journey={{ stageIds: journeyStageIds }}
            /*
              THE THREE THAT MOVED ONTO THE PAIR.

              Their work is done on the couple card — open a pair and the
              handover, the tenders and the button layout are steps on it,
              alongside Set up POS. A second card asking for the same three
              things is what this removes.

              HIDDEN HERE, NOT DELETED. These ids are load-bearing outside this
              screen: `merchantOrderProgress` names all three in its TILL_SPINE
              and gives them the customer-facing lines the MERCHANT reads
              ("Setting up how you take payment"), and `resolveJourney`
              auto-completes `button_layout` from the Set up POS stamp and
              `till_handover` from the handover record. Taking them out of
              ORDER_STAGES_V1 would strip three steps from every merchant's
              progress view and re-cut journeyProgress on orders already in
              flight, including in prod. They still resolve, still complete
              from the same evidence, and still count — they just no longer
              draw a card here.
            */
            hideStageIds={COUPLE_OWNED_STAGES}
            onComplete={journeyId ? async (stageId, note) => {
              const res = await journeyApi.complete(journeyId, stageId, note);
              setManual(res.manual);
            } : undefined}
            onOverride={journeyId ? async (stageId, reason) => {
              const res = await journeyApi.complete(journeyId, stageId, reason, undefined, true);
              setManual(res.manual);
            } : undefined}
            onUndo={journeyId ? async (stageId) => {
              const res = await journeyApi.undo(journeyId, stageId);
              setManual(res.manual);
            } : undefined}
            panels={{
              placed: (
                /* Devices, their offers, and the money — see OrderPlacedPanel. */
                <OrderPlacedPanel
                  tenantId={order.tenantId}
                  items={(order.items || []) as any}
                  models={models as any}
                  /* the order's own snapshot, taken at checkout — the offer
                     these devices were bought on, not the plan the tenant is
                     on today. Same field PlanPanel below prefers. */
                  orderDealCode={(order as any).dealCode}
                />
              ),
              // The plan they signed up on, from the deal.
              package: (
                <PlanPanel
                  tenantId={order.tenantId}
                  /* the order's own snapshot, taken at checkout — see DeviceOrderSchema */
                  orderDealCode={(order as any).dealCode}
                  orderAffiliateId={(order as any).soldByAffiliateId}
                  dealCode={(tenantMeta as any)?.dealCode}
                />
              ),
              /*
                WHAT THEY ACTUALLY SIGNED — every envelope of the checkout
                package, the documents inside each one, and the executed PDF
                to read on screen or take away. See AgreementPackagePanel.
              */
              agreement: (
                <AgreementPackagePanel
                  tenantId={order.tenantId}
                  orderEnvelopeId={order.envelopeId}
                  orderNumber={order.orderNo ? displayOrderNumber(order.orderNo) : undefined}
                />
              ),
              /*
                What this order's STORE pays, before and after it — from Stripe's
                live subscriptions, grouped by location. The account-wide
                onboarding projection stays as the fallback for when Stripe
                cannot be read, so the stage is never blank.
              */
              payment: (
                <LocationPaidPanel
                  order={order}
                  models={models}
                  orderLocationName={locationCtx?.location?.name || (locationCtx?.request as any)?.name}
                  fallback={(
                    <PaymentBreakdown
                      tenantId={order.tenantId}
                      units={units}
                      dueCents={(order.items || []).reduce(
                        (n: number, i: any) => n + (i.amountCents || 0) * (i.quantity || 1), 0)}
                    />
                  )}
                />
              ),
              /*
                The pack itself, as marks. No prose: the icons say it.

                UPLOADABLE FROM HERE, unlike the location card's read-only copy
                of the same component — a merchant who cannot work the uploader
                emails the file instead, and this is where somebody is already
                looking at what is missing.
              */
              compliance: locationCtx?.request?.documents?.length
                ? (
                  <DocumentMarks
                    documents={locationCtx.request.documents as any}
                    showBar={false}
                    onOpen={openComplianceDoc}
                    uploading={uploadingDoc}
                    onUpload={uploadComplianceDoc}
                    onDelete={clearComplianceDoc}
                    onDecide={decideComplianceDoc}
                    deciding={decidingDoc}
                  />
                )
                : undefined,
              // Allocate from stock, where the order is: pictures, counts,
              // scan or browse. Allocating here is what populates the
              // provisioning plans in the next stage.
              allocated: (
                <>
                  <AllocateStagePanel
                    order={order}
                    units={units}
                    models={models}
                    bundles={bundles}
                    /* A tick on a device's own checklist comes back with that
                       unit, so it is patched in place — a full reload would
                       remount the rail and lose the operator's place. */
                    onUnitChanged={patchUnit}
                    onAllocated={() => { load({ background: true }); onChanged(); }}
                  />
                  {/*
                    NO SECOND CHECKLIST HERE.

                    `stageChecklist('allocated')` drew the very same
                    ProvisioningStagePanel again, as one flat tree under the
                    allocation list — so a device's barcode was in one place on
                    this stage and its steps in another, and the same steps
                    appeared twice on one screen. The panel is now mounted per
                    DEVICE inside its own card by AllocateStagePanel, with the
                    same props and the same ticking; nothing was removed from
                    the stage except the duplicate.
                  */}
                </>
              ),
              /*
                THE PROVISIONING TREE, ONCE PER LANE.

                Same component, three `only` scopes. The card devices' plan is
                its own step now (`provision_payment`); `configure_payment` is
                the TID half alone, which MerchantPanel's terminal assignment
                already owns — so it gets no tree of its own here.
              */
              provision_payment: (
                <ProvisioningStagePanel
                  units={units}
                  models={models}
                  items={order.items as any}
                  bundles={bundles as any}
                  only="payment"
                  lane="payment"
                  stageIds={['provision_payment']}
                  paymentUnitIds={journeyContext.paymentUnitIds}
                  onUnitChanged={patchUnit}
                  onChanged={reload}
                />
              ),
              provisioned: (
                <ProvisioningStagePanel
                  units={units}
                  models={models}
                  items={order.items as any}
                  bundles={bundles as any}
                  only="hardware"
                  lane="pos"
                  stageIds={['provisioned']}
                  unitIds={journeyContext.posUnitIds}
                  paymentUnitIds={journeyContext.paymentUnitIds}
                  onUnitChanged={patchUnit}
                  onChanged={reload}
                />
              ),
              provision_accessories: (
                <ProvisioningStagePanel
                  units={units}
                  models={models}
                  items={order.items as any}
                  bundles={bundles as any}
                  only="hardware"
                  lane="accessory"
                  stageIds={['provision_accessories']}
                  unitIds={journeyContext.accessoryUnitIds}
                  paymentUnitIds={journeyContext.paymentUnitIds}
                  onUnitChanged={patchUnit}
                  onChanged={reload}
                />
              ),

              /*
                THE WORK ITSELF, on the lane that owns it. Each of these goes
                through the same endpoint its standalone screen uses — the
                order screen contributes only the thing it alone knows, which
                is WHAT this order needs and for which device.
              */
              /*
                THE HANDOVER — ours until here, the owner's from here. Sits
                before `packed` because a sealed box cannot have the card put
                in it.
              */
              /*
                THE THREE ON-TERMINAL STEPS, per device, after it is in the pool.

                `configure_payment` drew NOTHING before this — it resolved from
                the TID and rendered as a status line — so the work somebody
                physically does on a terminal appeared on no screen. It is drawn
                here rather than on `register_emv` because the ticks are stored
                against a pool device, and there is no device to store them on
                until registration has happened.
              */
              configure_payment: (
                <EmvConfigStepsPanel
                  tenantId={order.tenantId}
                  tenantName={tenantName}
                  locationId={order.locationId}
                  locationName={locationCtx?.request?.name}
                  units={units.filter((u) => journeyContext.paymentUnitIds?.includes(u.unitId))}
                  models={models}
                  /* The pool this screen already holds and already refreshes in
                     `refreshSetup` — which is the callback registration fires.
                     Passing it down is what makes a freshly-registered device
                     appear here without a page reload. */
                  pool={emvPool}
                  onChanged={refreshSetup}
                />
              ),
              register_emv: (
                <RegisterEmvPanel
                  tenantId={order.tenantId}
                  tenantName={tenantName}
                  locationId={order.locationId}
                  locationName={locationCtx?.request?.name}
                  units={units.filter((u) => journeyContext.paymentUnitIds?.includes(u.unitId))}
                  models={models}
                  varSheets={merchantApp?.varSheets || []}
                  /*
                    THE TWO THINGS A PERSON DOES ON THE TERMINAL, before it may
                    pair. Both are manual stages on this lane, so the answer is
                    simply whether each has been ticked — named one at a time so
                    the operator is told which one, not that "something" is
                    missing.
                  */
                  blockedReason={
                    !manual.emv_setup_config
                      ? 'Tick “Setup configurations” above first — the terminal has no profile in the processor’s portal yet.'
                      : !manual.emv_download_params
                        ? 'Tick “Download parameters” above first. A terminal that has not pulled its profile pairs fine and then fails at the first card.'
                        : undefined
                  }
                  onChanged={refreshSetup}
                />
              ),
              create_register: (
                <CreateRegisterPanel
                  tenantId={order.tenantId}
                  tenantName={tenantName}
                  locationId={order.locationId}
                  locationName={locationCtx?.request?.name}
                  units={units.filter((u) => journeyContext.posUnitIds?.includes(u.unitId))}
                  models={models}
                  onChanged={refreshSetup}
                />
              ),
              device_couple: (
                <>
                {/*
                  ONE SAVE BAR FOR EVERY PAIR. Each pair's Payment types card
                  writes into this hub, and the hub draws the one bar (and the
                  merchant's location values once) — not one bar per device.
                */}
                <PaymentTypesSaveHub
                  tenantId={order.tenantId}
                  locationId={order.locationId}
                  locationName={locationCtx?.request?.name}
                  tenantName={tenantName}
                  onSaved={refreshSetup}
                  showLocationValues
                >
                <DeviceCouplePanel
                  units={units}
                  models={models}
                  posUnitIds={journeyContext.posUnitIds || []}
                  paymentUnitIds={journeyContext.paymentUnitIds || []}
                  onUnitChanged={patchUnit}
                  /* The runner lives on this step: coupling is the last thing a
                     person decides before a till can be set up end to end. */
                  tenantId={order.tenantId}
                  locationId={order.locationId}
                  locationName={locationCtx?.request?.name}
                  ownerEmail={handoverEmail}
                  onSetupDone={refreshSetup}
                  /*
                    THE PAIR'S WORK, DRAWN ON THE PAIR.

                    These are the SAME panels the till_handover, payment_types
                    and button_layout stages mount — handed in as slots, not
                    reimplemented, and scoped to this one pair by the props
                    they already take. A slot is only called when its step is
                    opened, so an order with four pairs does not mount four
                    copies of a panel that reads every register on the tenant.
                  */
                  steps={{
                    handover: (pos) => (
                      <TillHandoverPanel
                        tenantId={order.tenantId}
                        tenantName={tenantName}
                        locationId={order.locationId}
                        locationName={locationCtx?.request?.name}
                        ownerEmail={(tenantMeta as any)?.ownerEmail || (tenantMeta as any)?.email}
                        ownerName={(tenantMeta as any)?.ownerName}
                        orderNumber={order.orderNo ? displayOrderNumber(order.orderNo) : undefined}
                        orderId={order.orderId}
                        orderStatus={order.status}
                        units={units}
                        models={models}
                        posUnitIds={[pos.unitId]}
                        terminals={registers}
                        onDone={refreshSetup}
                      />
                    ),
                    paymentTypes: (pos) => (
                      <PaymentTypesPanel
                        tenantId={order.tenantId}
                        locationId={order.locationId}
                        locationName={locationCtx?.request?.name}
                        tenantName={tenantName}
                        units={units}
                        models={models}
                        posUnitIds={[pos.unitId]}
                        refreshKey={setupTick + dataVersion}
                        onChanged={refreshSetup}
                      />
                    ),
                    buttons: (pos) => (
                      <ButtonLayoutPanel
                        tenantId={order.tenantId}
                        units={units}
                        posUnitIds={[pos.unitId]}
                        onChanged={refreshSetup}
                      />
                    ),
                    checks: (unit) => (
                      <ProvisioningStagePanel
                        units={[unit]}
                        unitIds={[unit.unitId]}
                        models={models}
                        stageIds={['device_couple']}
                        paymentUnitIds={journeyContext.paymentUnitIds}
                        onUnitChanged={patchUnit}
                        onChanged={reload}
                      />
                    ),
                  }}
                />
                </PaymentTypesSaveHub>
                </>
              ),
              /*
                MOVED ONTO THE PAIR — see `movedToCouple` above.

                These two drew PaymentTypesPanel and ButtonLayoutPanel over
                every POS device on the order. The couple stage now mounts the
                same two panels on the pair they belong to, so drawing them
                again here is a second place to do one job — which is how a
                register ends up with its tenders set on one screen and its
                layout on another.
              */
              /*
                THE TESTS, PROVED ON PAPER. Each is completed by uploading a
                photo of the receipt the till printed; with both in, receipt
                printing completes itself (resolveJourney).
              */
              test_transaction: (
                <>
                  {/*
                    PER-DEVICE CHECKS FIRST, GROUPED BY PAIR — what each
                    machine must pass before anybody runs the sale that proves
                    the set. The sale itself needs a till AND its reader, so
                    the pair is how the work is read.

                    The receipt panel below stays ORDER-LEVEL and single: a
                    journey mark is one per stage, so drawing it per pair would
                    show the same photo on every card and let one upload
                    overwrite another's. Per-pair receipts need a per-pair
                    mark, which is a service change, not a layout one.
                  */}
                  <CoupleChecklistStage
                    units={units}
                    models={models}
                    posUnitIds={journeyContext.posUnitIds || []}
                    paymentUnitIds={journeyContext.paymentUnitIds || []}
                    empty="No devices allocated to this order yet."
                    onUnitChanged={patchUnit}
                    /*
                      THE PROOF, ON THE PAIR THAT PRODUCED IT.

                      Each device's checks first, then the pair's own test and
                      refund receipts — as many photos as the slip needs. The
                      first photo of a kind also writes the stage's journey
                      mark, which is what `receipt_printing` resolves from and
                      what gates dispatch, so the rail behaves exactly as it
                      did while the evidence gets somewhere honest to live.
                    */
                    footer={(pair) => (
                      <PairReceipts
                        tenantId={order.tenantId}
                        orderId={order.orderId}
                        unit={pair.pos}
                        payment={pair.payment}
                        onUnitChanged={patchUnit}
                        onBarcodeVerified={async () => {
                          if (!journeyId || manual.barcode_scanning) return;
                          try {
                            const res = await journeyApi.complete(journeyId, 'barcode_scanning');
                            setManual(res.manual);
                          } catch { /* the check stands; the mark can be ticked by hand */ }
                        }}
                        onFirstOfKind={async (kind) => {
                          const stageId = kind === 'test' ? 'test_transaction' : 'refund_transaction';
                          if (!journeyId || manual[stageId]) return;
                          try {
                            const res = await journeyApi.complete(journeyId, stageId);
                            setManual(res.manual);
                          } catch { /* the photo is saved either way; the mark can be ticked by hand */ }
                        }}
                      />
                    )}
                  >
                    {(unit) => (
                      <ProvisioningStagePanel
                        units={[unit]}
                        unitIds={[unit.unitId]}
                        models={models}
                        stageIds={['test_transaction']}
                        paymentUnitIds={journeyContext.paymentUnitIds}
                        onUnitChanged={patchUnit}
                        onChanged={reload}
                      />
                    )}
                  </CoupleChecklistStage>
                  {/*
                    NO ORDER-WIDE RECEIPT UPLOAD HERE.

                    It took ONE photo for the whole order — on a four-pair
                    order the second upload replaced the first, and three
                    pairs' proof quietly vanished. Every pair now collects its
                    own test and refund photos, and its own barcode check,
                    above. The stage marks are still written on the first
                    photo of each kind, so `receipt_printing` still resolves
                    and dispatch still gates on it.
                  */}
                </>
              ),
              /*
                SHIPPING, PER BOX. The order's boxes (from packing) each take a
                carrier and tracking number; Mark shipped is refused until every
                box has one, then moves each device to Shipped with its own
                box's tracking — which is what sends the merchant's email.
              */
              /*
                PACKING INSTRUCTIONS, per device. There is no packing panel on
                this stage — the boxes are built in the packing view — so the
                per-device instructions ARE the stage's panel: what goes in the
                box with this machine, and how.
              */
              /*
                PACKING INSTRUCTIONS, per device but read pair by pair — a
                pair travels together unless somebody decides otherwise, and
                packing half of one is the mistake the coupling lookup exists
                to catch. Same per-device checklist as before; only the
                grouping is new.
              */
              packed: (
                <CoupleChecklistStage
                  units={units}
                  models={models}
                  posUnitIds={journeyContext.posUnitIds || []}
                  paymentUnitIds={journeyContext.paymentUnitIds || []}
                  empty="No devices allocated to this order yet."
                  onUnitChanged={patchUnit}
                >
                  {(unit) => (
                    <ProvisioningStagePanel
                      units={[unit]}
                      unitIds={[unit.unitId]}
                      models={models}
                      stageIds={['packed']}
                      paymentUnitIds={journeyContext.paymentUnitIds}
                      onUnitChanged={patchUnit}
                      onChanged={reload}
                    />
                  )}
                </CoupleChecklistStage>
              ),
              shipped: (
                <>
                {stageChecklist('shipped')}
                <ShippedStagePanel
                  orderId={order.orderId}
                  journeyId={journeyId}
                  units={units}
                  models={models}
                  shippedMark={manual.shipped}
                  onRecorded={setManual}
                  onUnitChanged={patchUnit}
                />
                </>
              ),
              delivered: (
                <ConfirmUnitsPanel
                  units={units}
                  models={models}
                  from="shipped"
                  to="delivered"
                  action="Confirm delivered"
                  explain="There is no carrier feed, so delivery is confirmed here. Only shipped devices can be confirmed — the stage completes when the last one is delivered."
                  onUnitChanged={patchUnit}
                  onDone={() => { load({ background: true }); onChanged(); }}
                />
              ),
              live: (
                <ConfirmUnitsPanel
                  units={units}
                  models={models}
                  from="delivered"
                  to="deployed"
                  action="Confirm live at the store"
                  explain="Confirm once the devices are set up and trading at the store. Only delivered devices can be confirmed."
                  onUnitChanged={patchUnit}
                  onDone={() => { load({ background: true }); onChanged(); }}
                />
              ),
              payment_verified: (
                <PaymentVerifyPanel
                  journeyId={journeyId}
                  tenantId={order.tenantId}
                  orderId={order.orderId}
                  units={units}
                  /*
                    The LINES, not a total. `amountCents` means a price on an
                    outright line and a monthly rate on a rented one, so summing
                    them produced a "due" figure that was neither — $630.00 of
                    bought hardware plus $247.97/mo of rented read as "$877.97
                    due", which nobody is charged. The panel splits them by sale
                    mode and reconciles each against Stripe.
                  */
                  orderItems={order.items as any}
                  completion={manual.payment_verified}
                  locationId={order.locationId}
                  addOnSubscriptionId={order.addOnSubscriptionId}
                  onChanged={setManual}
                />
              ),
              // The quick view (Task 7) — see PackagesActivatedStagePanel above.
              packages_activated: (
                <PackagesActivatedStagePanel onOpen={openPackagesQuickView} />
              ),
              /*
                THE PARTNER CHOICE, and nothing else. Once an ISO is picked
                this is settled history — the work moves to their side, which
                is the next stage.
              */
              merchant_assigned: order.locationId ? (
                <MerchantPanel
                  order={order}
                  units={units}
                  models={models}
                  locationName={locationCtx?.request?.name || tenantName}
                  onChanged={onChanged}
                  onApplication={setMerchantApp}
                  only="selection"
                />
              ) : undefined,
              /*
                ISO PROCESSING — the whole period the application is on the
                partner's side: the VAR sheet, the documents they ask the
                merchant for, and the three-way thread. This is where an order
                actually sits and waits, so it is where the conversation
                belongs; buried under the partner choice, nobody found it.
              */
              var_sheet: order.locationId ? (
                <div className="space-y-4">
                  {/*
                    WHICH SHEET PROGRAMS WHICH READER — the decision itself,
                    ahead of the thread and readiness furniture below. Needs a
                    loaded application (for its id), which MerchantPanel's own
                    load populates via onApplication.
                  */}
                  {merchantApp && (
                    <VarSheetBindingPanel
                      units={units}
                      models={models}
                      varSheets={merchantApp.varSheets}
                      applicationId={merchantApp.applicationId}
                      tenantId={order.tenantId}
                      tenantName={tenantName}
                      locationName={locationCtx?.request?.name}
                      locationId={order.locationId}
                      onChanged={setMerchantApp}
                    />
                  )}
                  <MerchantPanel
                    order={order}
                    units={units}
                    models={models}
                    locationName={locationCtx?.request?.name || tenantName}
                    onChanged={onChanged}
                    onApplication={setMerchantApp}
                    only="iso"
                  />
                </div>
              ) : undefined,
            }}
          />

          <div className="space-y-4">
            {order.locationId && (
              <OrderLocationCard
              tenantId={order.tenantId}
              locationId={order.locationId}
                tenantName={tenantName}
                onLoaded={setLocationCtx}
                documents={locationCtx?.request?.documents}
                onUpload={uploadComplianceDoc}
                onDelete={clearComplianceDoc}
                uploading={uploadingDoc}
              />
            )}

            {/*
              EVERYTHING SAID AND DONE about this order, in one place and for
              ever. Append-only: this is what a claim or a collection is argued
              from years later.
            */}
            {/*
              The order's own history, plus whatever happened to the STORE —
              a document verified on Registrations lands on the location's
              journey and is folded in here.
            */}
            <JourneyTimeline
              journeyId={journeyId}
              mergeSubject={order.locationId ? `location:${order.locationId}` : undefined}
              title="Order history"
              labels={timelineLabels}
              canPost
            />
          </div>
        </div>
      );
      })()}

      {/*
        THE CANCELLATION — directly below the order journey, because it is
        the same order's life continuing, not a separate screen. Full page
        width rather than the 360px timeline column: the worksheet is six
        columns wide and needs the room, scrolling inside its own box on a
        narrow viewport rather than pushing the page sideways.
      */}
      {order && journeyId && (
        <div className="mb-6">
          <CancellationPanel
            journeyId={journeyId}
            orderId={order.orderId}
            lines={cancellationLines}
            units={units}
            onRecordChange={setCancellationSummary}
            onUnitReleased={() => { load({ background: true }); onChanged(); }}
          />
        </div>
      )}

      {/*
        WHO EARNED ON THIS ORDER. Below the journey and the cancellation,
        above the devices: it is a fact ABOUT the order rather than a stage of
        it, and it renders unconditionally — an order with no partner says so
        in words. See CommissionPanel's own note on why the empty state is a
        statement and not a hidden section.
      */}
      {order && (
        <div className="mb-6">
          <CommissionPanel orderId={order.orderId} />
        </div>
      )}

      {/*
        DEVICES — read-only steps. They move from the events that make them true
        (allocation, provisioning, packing, Mark shipped, the Delivered and Live
        stages); the only manual control is a problem. See DeviceTracker.

        ACTIVATION sits just above: the order's destination (n of N devices
        unlocked and set up), with where and when each one was unlocked.
      */}
      <OrderActivationPanel orderId={order.orderId} order={order} tenantMeta={tenantMeta} refreshKey={dataVersion} />
      <DeviceTracker
        units={units}
        models={models}
        orderStatus={order.status}
        drafts={drafts}
        draftOf={draftOf}
        onDraft={patch}
        renderThumb={(unit, model) => {
          const Icon = CATEGORY_ICON[model?.category || ''] || Package;
          return (
            <ShimmerImage
              src={model?.media?.heroImageUrl}
              alt={model?.modelName || unit.modelId}
              width={40}
              height={40}
              objectFit="contain"
              className="shrink-0 rounded-lg"
              fallbackBgColor="bg-gray-900"
              fallbackIcon={<Icon size={16} className="text-gray-400" />}
              variant="dark"
            />
          );
        }}
      />

      {/* The order status AS STORED, with `partial` explained in words. Never the
          drafts: what an operator has clicked but not saved is not yet true. */}
      <div className={`${previewDiffers ? 'mb-2' : 'mb-4'} rounded-lg border border-blue-200 bg-blue-50 p-2.5 text-[11px] leading-relaxed text-blue-900`}>
        <strong>{derived.headline}</strong> — {derived.detail}
      </div>

      {/* The preview, kept visibly separate and never dressed up as fact. */}
      {previewDiffers && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900">
          <strong>Not saved yet.</strong> Save these changes and the order would read{' '}
          <strong>{preview.label}</strong>. Until you do, the status above is what the order is and
          what the customer has been told.
        </div>
      )}

      <p className="mb-4 text-[11px] leading-relaxed text-gray-500">
        <strong>On hold</strong> and <strong>backordered</strong> need a reason, and the merchant is shown it word for
        word. Recording a problem emails the merchant from <strong>Rubytill</strong>.
      </p>

      {/* Cancelling is not undoable, and a native confirm() is silently a no-op
          inside a webview — so the confirmation is part of the page. */}
      {confirmCancel && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-[11px] text-red-900">
          <span><strong>Cancel this order?</strong> This cannot be undone, and no device on it may move through fulfilment afterwards.</span>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmCancel(false)}
              className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 text-xs font-semibold text-gray-700"
            >
              Keep order
            </button>
            <button
              onClick={cancelOrder}
              className="min-h-[44px] rounded-lg bg-red-600 px-4 text-xs font-semibold text-white"
            >
              Yes, cancel order
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {/* A disabled button with no reason is a dead end — always say why, as
            TEXT. A `title` never fires on a touch device, and this screen's own
            44px targets say touch is expected. Save's reason comes first
            because it blocks the action the operator is mid-way through; the
            cancel reason is next, and only then the quiet "nothing to save". */}
        {!saving && (blocked || cancelBlocked || pending.length === 0) && (
          <p className="flex-1 text-[11px] leading-relaxed">
            {blocked ? (
              <span className="text-red-700">{blocked}</span>
            ) : cancelBlocked ? (
              <span className="text-gray-500">
                <strong className="text-gray-700">Cancel unavailable:</strong> {cancelBlocked}
                {pending.length === 0 ? ' No unsaved changes.' : ''}
              </span>
            ) : (
              <span className="text-gray-500">No unsaved changes.</span>
            )}
          </p>
        )}
        <button
          onClick={() => setConfirmCancel(true)}
          disabled={!!cancelBlocked || confirmCancel}
          title={cancelBlocked || 'Cancel this order'}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Cancel order
        </button>
        <button
          onClick={save}
          disabled={saving || pending.length === 0 || !!blocked}
          title={blocked || (pending.length === 0 ? 'Nothing to save' : 'Save these device changes')}
          className="flex min-h-[44px] items-center gap-2 rounded-lg px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          style={{ background: '#DC143C' }}
        >
          {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <CheckCircle2 size={15} />}
          {shipping ? 'Save & mark shipped' : 'Save changes'}
        </button>
      </div>

      {/* The `packages_activated` quick view (Task 7) — its own fixed
          overlay, so its position in this tree doesn't matter. `onRan`
          refreshes THIS screen's copy of the activation runs after a
          successful retry, so the timeline pill updates without a full page
          reload — see `loadActivationRuns` above. */}
      <PackageActivationQuickView
        tenantId={order.tenantId}
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        onRan={() => loadActivationRuns(order.tenantId)}
      />
    </div>
    </DataVersionContext.Provider>
  );
};

/**
 * The order screen, inside the host an open Replace wizard lives in. The host
 * sits ABOVE every loading/error branch and every row, so neither a reload of
 * the order nor a swap taking the device off it can unmount a wizard mid-run
 * (see replaceWizardHost).
 */
const OrderDetail: React.FC<Props> = (props) => (
  <ReplaceWizardHost>
    <OrderDetailBody {...props} />
  </ReplaceWizardHost>
);

export default OrderDetail;
